/**
 * Per-app pricing configuration for the Quantrolix ecosystem.
 * Updated per Council Decision #10 (revised: nothing free).
 *
 * Usage:
 *   import { getAppPricing } from '@shared/subscription-gate/pricing-config';
 *   const pricing = getAppPricing('fire-pos');
 */

const APP_PRICING = {
  'fire-pos': {
    name: 'Fire POS',
    model: 'b2b',
    trialDays: 14,
    tiers: [
      { id: 'starter', label: 'Starter', price: 49, interval: 'month', features: ['1 location', 'Basic POS', '5 users'] },
      { id: 'professional', label: 'Professional', price: 99, interval: 'month', features: ['3 locations', 'KDS', 'Analytics', '15 users'], recommended: true },
      { id: 'enterprise', label: 'Enterprise', price: 199, interval: 'month', features: ['Unlimited locations', 'API access', 'Custom integrations', 'Unlimited users'] },
    ],
    annualDiscount: 0.20,
  },
  'yieldvault': {
    name: 'YieldVault',
    model: 'b2b',
    trialDays: 14,
    tiers: [
      { id: 'starter', label: 'Starter', price: 29, interval: 'month', features: ['1 location', 'Basic yields', '5 users'] },
      { id: 'professional', label: 'Professional', price: 79, interval: 'month', features: ['3 locations', 'AI insights', '15 users'], recommended: true },
      { id: 'enterprise', label: 'Enterprise', price: 199, interval: 'month', features: ['Unlimited locations', 'API access', 'Unlimited users'] },
    ],
    annualDiscount: 0.25,
  },
  'grocery-optimizer': {
    name: 'Grocery Optimizer',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['AI meal planning', 'Instacart integration', 'Macro optimization', 'Price comparison'] },
      { id: 'annual', label: 'Annual', price: 39.99, interval: 'year', features: ['Everything in Monthly', 'Save 33%'], recommended: true },
    ],
  },
  'tea-effects': {
    name: 'Tea Effects',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 2.99, interval: 'month', features: ['Full tea database', 'Health tracking', 'AI recommendations'] },
      { id: 'annual', label: 'Annual', price: 24.99, interval: 'year', features: ['Everything in Monthly', 'Save 30%'], recommended: true },
    ],
  },
  'phantomcast': {
    name: 'PhantomCast',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 3.99, interval: 'month', features: ['Multi-room streaming', 'Horror movie integration', 'Unlimited devices'] },
    ],
  },
  'kasa-controller': {
    name: 'Kasa Controller',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 2.99, interval: 'month', features: ['Unlimited devices', 'Schedules', 'Energy monitoring'] },
    ],
  },
  'bourbon-hunter': {
    name: 'Bourbon Hunter',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 2.99, interval: 'month', features: ['Full bourbon database', 'Price alerts', 'Collection tracking'] },
    ],
  },
  'nexus-3d-maps': {
    name: 'Nexus 3D Maps',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['3D globe visualization', 'Custom data layers', 'Export maps'] },
    ],
  },
  'couplescare': {
    name: 'CouplesCare',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 3.99, interval: 'month', features: ['AI therapy sessions', 'Mood tracking', 'Guided exercises'] },
    ],
  },
  'menumentor': {
    name: 'MenuMentor',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['AI flashcards', 'Voice training', 'Wine & spirits decks'] },
    ],
  },
  'motionforge': {
    name: 'MotionForge',
    model: 'prosumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 9.99, interval: 'month', features: ['Video editing', 'Remotion templates', 'Export HD/4K'] },
    ],
  },
  'architect-3d': {
    name: 'Architect 3D',
    model: 'prosumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 9.99, interval: 'month', features: ['3D design tools', 'Floor plans', 'AR preview'] },
    ],
  },
  'qode-ide': {
    name: 'Qode IDE',
    model: 'developer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['AI code editor', 'Terminal', 'Git integration'] },
    ],
  },
  'clausely': {
    name: 'Clausely',
    model: 'consumer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['AI contract review', 'Risk scoring', 'Standard form library'] },
    ],
  },
  'api-marketplace': {
    name: 'API Marketplace',
    model: 'developer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 9.99, interval: 'month', features: ['API access', 'Rate limiting', 'Usage analytics'] },
    ],
  },
  'quantrolix-launcher': {
    name: 'Quantrolix Launcher',
    model: 'ecosystem',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 2.99, interval: 'month', features: ['App hub', 'Auto-updates', 'Comm Hub access'] },
    ],
  },
  'claude-blob': {
    name: 'Claude Blob',
    model: 'developer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['AI IDE', 'Spark assistant', 'Project management'] },
    ],
  },
  'comm-hub': {
    name: 'Comm Hub',
    model: 'developer',
    trialDays: 7,
    tiers: [
      { id: 'monthly', label: 'Monthly', price: 4.99, interval: 'month', features: ['Multi-agent coordination', 'Task management', 'Real-time sync'] },
    ],
  },
};

export function getAppPricing(appId) {
  return APP_PRICING[appId] || null;
}

export function getAllPricing() {
  return APP_PRICING;
}

export default APP_PRICING;
